/**
 * Shared helpers for bulk-moving tasks between projects from Cards and
 * List views. A "task" is a `(group_name, sub_group_name)` bundle that
 * may contain several usecase rows (composed / multi-objective).
 */

export const usecaseBundleKey = (usecase) => {
    const group = usecase?.group_name || usecase?._groupName || '';
    const sub =
        (usecase?.sub_group_name || '').trim() ||
        usecase?.usecase_name ||
        usecase?._id ||
        '';
    return `${group}::${sub}`;
};

export const taskSelectionKey = (subGroup, groupName = '') => {
    const sub =
        (subGroup?.sub_group_name || '').trim() ||
        subGroup?.usecases?.[0]?.usecase_name ||
        subGroup?.usecases?.[0]?._id ||
        '';
    return `${groupName}::${sub}`;
};

/**
 * Destination projects for the Move modal. When every selected item
 * already lives in one project, that project is omitted (Figma: other
 * projects only). Mixed-project selections keep every project so the
 * user can gather them in one place.
 */
export const getMoveDestinations = (allApps, selectedGroupNames = []) => {
    const uniqueSources = [...new Set((selectedGroupNames || []).filter(Boolean))];
    const onlySource = uniqueSources.length === 1 ? uniqueSources[0] : null;

    return (allApps || [])
        .filter((app) => app?.group_name && app.group_name !== onlySource)
        .map((app) => ({
            name: app.group_name,
            count: (app.sub_groups || []).length,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Collapse selected usecases into update_llm_usecase calls.
 *
 * When every sibling of a composed task is selected, one call with
 * `propagate_sub_group_name` moves the whole bundle. A partial
 * selection updates each row on its own so unselected siblings stay.
 */
export const planBulkMove = (selectedUsecases = [], allUsecases = []) => {
    const allByBundle = new Map();
    (allUsecases || []).forEach((usecase) => {
        if (!usecase?._id) return;
        const key = usecaseBundleKey(usecase);
        if (!allByBundle.has(key)) allByBundle.set(key, []);
        allByBundle.get(key).push(usecase);
    });

    const selectedByBundle = new Map();
    (selectedUsecases || []).forEach((usecase) => {
        if (!usecase?._id) return;
        const key = usecaseBundleKey(usecase);
        if (!selectedByBundle.has(key)) selectedByBundle.set(key, []);
        selectedByBundle.get(key).push(usecase);
    });

    const ops = [];
    selectedByBundle.forEach((selected) => {
        const key = usecaseBundleKey(selected[0]);
        const siblings = allByBundle.get(key) || selected;
        const selectedIds = new Set(selected.map((item) => item._id));
        const movingWholeTask =
            siblings.length > 0 && siblings.every((item) => selectedIds.has(item._id));

        if (movingWholeTask) {
            ops.push({ usecase: selected[0], propagate: true });
            return;
        }
        selected.forEach((usecase) => {
            ops.push({ usecase, propagate: false });
        });
    });
    return ops;
};

/**
 * Run the `update_llm_usecase` calls produced by `planBulkMove`.
 * Callers own toasts and refresh. `postFn` is the HTTP helper so tests
 * can inject a stub without importing the client.
 */
export const runBulkMove = async (usecases, destGroupName, allUsecases, postFn) => {
    if (!destGroupName || !Array.isArray(usecases) || usecases.length === 0) {
        return { success: false, moved: 0, failures: ['No tasks selected to move'] };
    }

    const ops = planBulkMove(usecases, allUsecases);
    const failures = [];
    let moved = 0;

    for (const op of ops) {
        // Sequential: destination uniqueness is checked per call.
        // eslint-disable-next-line no-await-in-loop
        const result = await postFn(
            'update_llm_usecase',
            buildMoveUpdatePayload(op.usecase, destGroupName, op.propagate)
        );
        if (result?.success) {
            moved += 1;
        } else {
            failures.push(result?.message || 'Failed to move task');
        }
    }

    return {
        success: failures.length === 0,
        moved,
        failures,
    };
};

export const buildMoveUpdatePayload = (usecase, destGroupName, propagate) => ({
    usecase_id: usecase._id,
    client_id: usecase.client_id,
    usecase_name: usecase.usecase_name,
    usecase_goal: usecase.usecase_goal ?? null,
    task_type: usecase.task_type ?? null,
    group_name: destGroupName,
    sub_group_name: (usecase.sub_group_name || '').trim() || null,
    propagate_sub_group_name: !!propagate,
    is_multilabel: usecase.is_multilabel || false,
    classify_speakers: usecase.classify_speakers || false,
    confidence_rejection_enabled: usecase.confidence_rejection_enabled !== false,
    rejection_label_name: usecase.rejection_label_name ?? null,
    task_languages: usecase.task_languages || [],
});
